import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  User, 
  Building2, 
  GraduationCap,
  Users,
  ArrowRight,
  CheckCircle,
  Briefcase
} from 'lucide-react';
import logo from '../../assets/logo.png';

interface RoleOption {
  id: 'user' | 'training_center' | 'consultant';
  title: string;
  subtitle: string;
  icon: React.ElementType;
  description: string;
  features: string[];
  color: string;
  borderColor: string;
  iconColor: string;
  buttonColor: string;
  buttonHoverColor: string;
}

const RoleSelectionPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const roles: RoleOption[] = [
    {
      id: 'user',
      title: t('auth.roleSelectionPage.roles.user.title', 'Pharma Professional'),
      subtitle: t('auth.roleSelectionPage.roles.user.subtitle', 'Develop your skills'),
      icon: User,
      description: t('auth.roleSelectionPage.roles.user.description', 'Access specialized training and certify your expertise'),
      features: t('auth.roleSelectionPage.roles.user.features', { returnObjects: true, defaultValue: ['Certified trainings', 'Personalized follow-up', 'Recognized certificates'] }) as string[],
      color: 'from-brand-surface to-purple-50',
      borderColor: 'border-brand-primary/20 hover:border-brand-primary/40',
      iconColor: 'text-brand-primary',
      buttonColor: 'bg-brand-primary hover:bg-brand-primary/90',
      buttonHoverColor: 'hover:bg-brand-primary/90'
    },
    {
      id: 'training_center',
      title: t('auth.roleSelectionPage.roles.training_center.title', 'Training Center'),
      subtitle: t('auth.roleSelectionPage.roles.training_center.subtitle', 'Publish your programs'),
      icon: Building2,
      description: t('auth.roleSelectionPage.roles.training_center.description', 'Showcase your trainings and reach a targeted audience'),
      features: t('auth.roleSelectionPage.roles.training_center.features', { returnObjects: true, defaultValue: ['Unlimited publishing', 'Enrollment management', 'National visibility'] }) as string[],
      color: 'from-brand-surface to-pink-50',
      borderColor: 'border-brand-secondary/20 hover:border-brand-secondary/40',
      iconColor: 'text-brand-secondary',
      buttonColor: 'bg-brand-secondary hover:bg-brand-secondary/90',
      buttonHoverColor: 'hover:bg-brand-secondary/90'
    },
    {
      id: 'consultant',
      title: t('auth.roleSelectionPage.roles.consultant.title', 'Consultant'),
      subtitle: t('auth.roleSelectionPage.roles.consultant.subtitle', 'Share your expertise'),
      icon: Briefcase,
      description: t('auth.roleSelectionPage.roles.consultant.description', 'Offer your consulting and support services'),
      features: t('auth.roleSelectionPage.roles.consultant.features', { returnObjects: true, defaultValue: ['Professional profile', 'Matching', 'Recognized expertise'] }) as string[],
      color: 'from-brand-surface to-mint-50',
      borderColor: 'border-brand-mint/20 hover:border-brand-mint/40',
      iconColor: 'text-brand-mint',
      buttonColor: 'bg-brand-mint hover:bg-brand-mint/90 text-gray-900',
      buttonHoverColor: 'hover:bg-brand-mint/90'
    }
  ];

  const handleRoleSelect = (roleId: 'user' | 'training_center' | 'consultant') => {
    if (roleId === 'user') {
      navigate('/auth/user');
    } else if (roleId === 'training_center') {
      navigate('/auth/training-center');
    } else if (roleId === 'consultant') {
      navigate('/auth/consultant');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-surface via-white to-gray-50">
      {/* Header */}
      <div className="relative z-10 pt-8 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Logo */}
          <div className="flex items-center justify-center mb-16">
            <img src={logo} alt="PHINLEX" className="h-10 w-auto" />
          
          </div>

          {/* Title Section */}
          <div className="text-center mb-16">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              {t('auth.roleSelectionPage.title', 'Choose your profile')}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
            >
              {t('auth.roleSelectionPage.subtitle', "Join Algeria's pharmaceutical training platform")}
            </motion.p>
          </div>

          {/* Stats Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap justify-center gap-8 mb-16"
          >
            <div className="text-center">
              <div className="text-3xl font-bold text-brand-primary mb-2">150+</div>
              <div className="text-sm text-gray-600">{t('auth.roleSelectionPage.stats.formations')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-brand-primary mb-2">50+</div>
              <div className="text-sm text-gray-600"> {t('auth.roleSelectionPage.stats.centers')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-brand-primary mb-2">1000+</div>
              <div className="text-sm text-gray-600">  {t('auth.roleSelectionPage.stats.professionals')}</div>
            </div>
          </motion.div>

          {/* Role Selection Cards */}
          <div className="max-w-6xl mx-auto">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="grid md:grid-cols-3 gap-6"
            >
              {roles.map((role, index) => (
                <motion.div
                  key={role.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className={`relative bg-gradient-to-br ${role.color} rounded-2xl border-2 ${role.borderColor} p-6 transition-all duration-300 hover:shadow-xl hover:scale-105 group`}
                >
                  {/* Icon */}
                  <div className={`w-12 h-12 ${role.iconColor} bg-white rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <role.icon className="h-6 w-6" />
                  </div>

                  {/* Header */}
                  <div className="mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{role.title}</h3>
                    <p className="text-gray-600 text-sm">{role.subtitle}</p>
                  </div>

                  {/* Description */}
                  <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                    {role.description}
                  </p>

                  {/* Features */}
                  <div className="mb-4">
                    <ul className="space-y-1">
                      {role.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center gap-2">
                          <CheckCircle className={`h-3 w-3 ${role.iconColor} flex-shrink-0`} />
                          <span className="text-gray-700 text-xs">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={() => handleRoleSelect(role.id)}
                    className={`w-full ${role.buttonColor} py-3 px-6 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-3 group-hover:shadow-lg`}
                  >
                    {t('auth.roleSelectionPage.buttons.continue', 'Continue')}
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Additional Info */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-center mt-16"
          >
            <p className="text-gray-600 mb-6">
              <button 
                onClick={() => navigate('/')}
                className="text-brand-primary hover:underline font-semibold"
              >
                {t('auth.roleSelectionPage.footer.backHome', 'Back to homepage')}
              </button>
            </p>
            
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>{t('auth.roleSelectionPage.footer.secure', 'Secure and certified platform')}</span>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RoleSelectionPage;
